import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Grid, Menu, MenuItem, ListItemIcon, useTheme } from '@mui/material';
import { Typography, TextField, Button, DialogContent, DialogTitle, Avatar } from '@mui/material';
import { EditRounded, SubjectRounded, FiberManualRecord, VisibilityRounded } from '@mui/icons-material';
import { PersonRounded, ArrowDropDownRounded, PercentRounded } from '@mui/icons-material';
import { DescriptionRounded, CheckCircleOutlineRounded, NotInterested } from '@mui/icons-material';
import { grey, green } from '@mui/material/colors';
import { TimePicker, DatePicker, RoundedDialog, StandardDialogActions, UserGroup } from '../../Components';
import { TextDialog, DateTimePicker, SearchMenu, TooltipTypography } from '../../Components';
import FilesTable from '../../../Shared/Files/FilesTable';
import StateManager from '../../StateManager';
import { FormatOnlyTime, FormatOnlyDate, validateEmail, validateUkNumber, isUuid, isHex24 } from '../../Functions';
import { FormatDateFull, HtmlToPlainText } from '../../Functions';
import Autocomplete from '@mui/material/Autocomplete';
import { isEqual, isNumber, isArray, isEmpty, cloneDeep, difference, isString, round } from 'lodash';
import moment from 'moment';
import ActionGroupButton from '../ActionGroupButton';
import ReadAndSignDialog from '../ReadAndSignDialog';
import TabbedSignature from '../TabbedSignature';
import DataSetCell from '../DataSetCell';
import ColorCheckCell from './ColorCheckCell';
import DefectCheckCell from './DefectCheckCell';
import StockField from '../StockField';
import CellActionButton from './CellActionButton';
import TickboxCell from './TickboxCell';
//import RefLinkDialog from '../RefLinkDialog';

const ISO_DATE_FORMAT = 'YYYY-MM-DD';

const GridCell = React.memo(
  ({
    column,
    initial,
    onChange,
    editable,
    users,
    building,
    withTimeout,
    row,
    onDataChange,
    textAlign,
    activityInfo,
    allCreatedActions,
    onCreatedAction,
    onDeletedAction,
    disableClick,
    forRegister,
    textWrapping,
    disabled,
    actionsDisabled,
  }) => {
    const theme = useTheme();
    const timer = useRef(null);
    const [columnType, setColumnType] = useState(null);
    const [text, setText] = useState('');
    const [error, setError] = useState(false);
    const [number, setNumber] = useState(null);
    const [date, setDate] = useState(null);
    const [dropboxValue, setDropboxValue] = useState('');
    const [userIds, setUserIds] = useState([]);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [files, setFiles] = useState([]);
    const [signature, setSignature] = useState({});
    const [dataSetValues, setDataSetValues] = useState([]);
    const [dataSetRows, setDataSetRows] = useState([]);
    const [statusValue, setStatusValue] = useState('');
    const [calculationType, setCalculationType] = useState(null);
    const [rnsDialog, setRnsDialog] = useState(null);
    //    const [linkedFeatureDialog, setLinkedFeatureDialog] = useState(null);
    const invitationId = localStorage.getItem('invitationId');
    const currUserId = localStorage.getItem('_id');
    const columnWidth = column?.width || 250;
    const SAVING_TIMEOUT = column?.fieldType === 'number' ? 800 : 1600;
    const prev = useRef(null);

    // text for the button
    const textAreaPreview = useMemo(() => {
      if (!text || column?.fieldType !== 'textArea') return text;

      const result = HtmlToPlainText(text, 100, true);

      return result;
    }, [text, column?.fieldType]);

    function triggerChange(data, sendInstantly) {
      if (typeof onChange !== 'function') return;
      if (withTimeout && !sendInstantly) {
        if (timer.current) {
          clearTimeout(timer.current);
        }
        timer.current = setTimeout(() => {
          if (isEqual(data, prev.current)) return;
          if (column.fieldType === 'phone' && data.value && !validateUkNumber(data.value)) return;
          if (column.fieldType === 'email' && data.value && !validateEmail(data.value)) return;
          prev.current = cloneDeep(data);
          onChange(data);
        }, SAVING_TIMEOUT);
      } else {
        if (isEqual(data, prev.current)) return;
        prev.current = cloneDeep(data);
        onChange(data);
      }
    }

    const noValue = null;

    function getObjectType(obj) {
      if (obj == null) return null;
      if (!isNaN(obj) && !isNaN(parseFloat(obj))) return 'number';
      const date = moment(obj, true);
      if (date.isValid()) {
        return 'date';
      }

      return 'time';
    }

    useEffect(() => {
      setColumnType(column.fieldType);

      const value = initial?.value;
      prev.current = cloneDeep(initial);
      if (!column.fieldType) return;
      if (['textArea', 'text', 'email', 'phone'].includes(column.fieldType)) {
        setText(isString(value) || isNumber(value) ? String(value) : '');
      }
      if (column.fieldType === 'number') {
        setNumber(isNumber(value) ? value : '');
      }
      if (column.fieldType === 'calculation') {
        const type = getObjectType(value);
        setCalculationType(type);
        if (type === 'date') {
          setDate(moment(value).format(ISO_DATE_FORMAT));
        } else if (type === 'time') {
          setText(value);
        } else {
          setNumber(value == null ? null : Number(value));
        }
      }
      if (column.fieldType === 'time') {
        setDate(
          value && moment(value, true).isValid()
            ? moment(value).set({ year: 2000, month: 0, date: 1, seconds: 0, milliseconds: 0 }).toISOString()
            : null,
        );
      }
      if (column.fieldType === 'date') {
        setDate(value && moment(value, true).isValid() ? moment(value).format(ISO_DATE_FORMAT) : null);
      }
      if (column.fieldType === 'datetime') {
        setDate(value && moment(value, true).isValid() ? moment(value) : null);
      }
      if (column.fieldType === 'dropbox' || column.fieldType === 'weightedList') {
        setDropboxValue(isUuid(value) ? value : '');
      }
      if (column.fieldType === 'user') {
        setUserIds(isArray(value) ? value : isHex24(value) ? [value] : []);
      }
      if (column.fieldType === 'signature') {
        setSignature(value?.signature ? value?.signature : value?.id ? value : {});
      }
      if (column.fieldType === 'file' || column.fieldType === 'image') {
        setFiles(isArray(value) ? value : []);
      }
      if (column.fieldType === 'dataSet') {
        if (isArray(initial?.rows)) {
          setDataSetRows(initial.rows);
          setDataSetValues(initial.rows.map((x) => x.id));
        }
        if (isArray(initial?.value?.rows)) {
          setDataSetRows(initial.value.rows);
          setDataSetValues(initial.value.rows.map((x) => x.id));
        }
      }
      if (column.fieldType === 'status') {
        setStatusValue(isUuid(value) ? value : '');
      }
      //eslint-disable-next-line
    }, [column?.fieldType, column?.stockOptions, initial]);

    function selectUser() {
      StateManager.selectMultipleUsers(
        (res) => {
          if (res.users) {
            setUserIds(res.users);
          }
          triggerChange({ value: res.users }, true);
        },
        { initiallySelected: userIds },
      );
    }

    function onDataSetResult({ rows }) {
      const value = rows.map((x) => x.id);
      setDataSetValues(value);
      setDataSetRows(rows);
      triggerChange({ value, rows });
    }

    const formatTime = useCallback(() => FormatOnlyTime(date), [date]);
    const formatDate = useCallback(() => FormatOnlyDate(date), [date]);
    const formatDateTime = useCallback(() => FormatDateFull(date), [date]);

    if (!columnType) return null;

    const containerProps = { container: true, justifyContent: textAlign };

    if (columnType === 'text') {
      if (editable) {
        return (
          <Grid {...containerProps}>
            <TextField
              variant="standard"
              fullWidth
              value={text || ''}
              id={`textfield-${column.id}-${row?.id}`}
              onKeyDown={(e) => e.stopPropagation()} // critical
              onChange={(e) => {
                setText(e.target.value);
                triggerChange({ value: e.target.value });
              }}
              inputProps={{ maxLength: 1024, style: { textAlign } }}
              disabled={Boolean(disabled)}
            />
          </Grid>
        );
      }

      return <Grid {...containerProps}>{text != null ? <TooltipTypography>{text}</TooltipTypography> : noValue}</Grid>;
    }
    if (columnType === 'email') {
      if (editable) {
        return (
          <Grid {...containerProps}>
            <TextField
              variant="standard"
              placeholder="Email"
              onKeyDown={(e) => e.stopPropagation()} // critical
              fullWidth
              value={text || ''}
              type="email"
              id={`textfield-${column.id}-${row?.id}`}
              error={error && !!text}
              helperText={error && !!text ? 'Enter a correct email' : ''}
              onChange={(e) => {
                const email = String(e.target.value).toLowerCase();
                const valid = validateEmail(email);
                setError(!valid);
                setText(email);
                triggerChange({ value: email });
              }}
              inputProps={{ maxLength: 128, style: { textAlign } }}
              disabled={Boolean(disabled)}
            />
          </Grid>
        );
      }

      return <Grid {...containerProps}>{text != null ? <TooltipTypography>{text}</TooltipTypography> : noValue}</Grid>;
    }
    if (columnType === 'phone') {
      if (editable) {
        return (
          <Grid {...containerProps}>
            <TextField
              placeholder="Phone number"
              onKeyDown={(e) => e.stopPropagation()} // critical
              variant="standard"
              fullWidth
              value={text || ''}
              type="tel"
              id={`textfield-${column.id}-${row?.id}`}
              error={error && !!text}
              helperText={error && !!text ? 'Enter a correct phone number' : ''}
              onChange={(e) => {
                const value = String(e.target.value).replace(/[^\d+]/g, '');
                const valid = validateUkNumber(value);
                setError(!valid);
                setText(value);
                triggerChange({ value });
              }}
              inputProps={{ maxLength: 128, style: { textAlign } }}
              disabled={Boolean(disabled)}
            />
          </Grid>
        );
      }

      return <Grid {...containerProps}>{text != null ? <TooltipTypography>{text}</TooltipTypography> : noValue}</Grid>;
    }

    if (columnType === 'number') {
      if (editable) {
        return (
          <Grid {...containerProps}>
            <TextField
              fullWidth
              type="number"
              onKeyDown={(e) => e.stopPropagation()} // critical
              variant="standard"
              value={number == null ? '' : number}
              onChange={(e) => {
                const value = e.target.value === '' ? '' : Number(e.target.value);
                setNumber(value);
                triggerChange({ value });
              }}
              id={`textfield-${column.id}-${row?.id}`}
              onWheel={(e) => e.target.blur()}
              inputProps={{ maxLength: 64, style: { textAlign } }}
              disabled={Boolean(disabled)}
              InputProps={{
                startAdornment: column.numberOptions ? (
                  <>
                    {column.numberOptions?.format === 'dollar' && <>$</>}
                    {column.numberOptions?.format === 'pound' && <>£</>}
                    {column.numberOptions?.format === 'euro' && <>€</>}
                    {column.numberOptions?.format === 'percent' && <>%</>}
                  </>
                ) : null,
              }}
            />
          </Grid>
        );
      }

      return (
        <Grid {...containerProps}>
          {number != null ? (
            <>
              {number != null && (
                <>
                  {column.numberOptions?.format === 'dollar' && <>$</>}
                  {column.numberOptions?.format === 'pound' && <>£</>}
                  {column.numberOptions?.format === 'euro' && <>€</>}
                </>
              )}

              <Typography>
                {['dollar', 'pound', 'euro'].includes(column.numberOptions?.format)
                  ? Number(number).toFixed(2)
                  : number}
              </Typography>
              {number != null && column.numberOptions?.format === 'percent' && <PercentRounded fontSize="small" />}
            </>
          ) : (
            noValue
          )}
        </Grid>
      );
    }

    if (columnType === 'time') {
      if (editable) {
        return (
          <Grid {...containerProps}>
            <TimePicker
              value={date}
              label={date ? '' : undefined}
              disablePadding
              id={`textfield-${column.id}-${row?.id}`}
              fullWidth
              onChange={(time) => {
                const value = moment(time)
                  .set({ year: 2000, month: 0, date: 1, seconds: 0, milliseconds: 0 })
                  .toISOString();
                setDate(value);
                triggerChange({ value }, true);
              }}
              inputProps={{ style: { textAlign } }}
              disabled={Boolean(disabled)}
            />
          </Grid>
        );
      }

      return (
        <Grid {...containerProps}>
          {date ? <Typography style={{ fontWeight: 500 }}>{formatTime(date)}</Typography> : noValue}
        </Grid>
      );
    }

    if (columnType === 'date') {
      if (editable) {
        return (
          <DatePicker
            value={date}
            label={date ? '' : undefined}
            disablePadding
            id={`textfield-${column.id}-${row?.id}`}
            fullWidth
            onChange={(date) => {
              const value = moment(date).format(ISO_DATE_FORMAT);
              setDate(value);
              triggerChange({ value }, true);
            }}
            inputProps={{ style: { textAlign } }}
            disabled={Boolean(disabled)}
          />
        );
      }

      return (
        <Grid {...containerProps}>
          {date ? <Typography style={{ fontWeight: 500 }}>{formatDate(date)}</Typography> : noValue}
        </Grid>
      );
    }

    if (columnType === 'datetime') {
      if (editable) {
        return (
          <Grid {...containerProps}>
            <DateTimePicker
              value={date}
              label={date ? '' : undefined}
              id={`textfield-${column.id}-${row?.id}`}
              disablePadding
              fullWidth
              onChange={(date) => {
                setDate(date);
                triggerChange({ value: date });
              }}
              inputProps={{ style: { textAlign } }}
              disabled={Boolean(disabled)}
            />
          </Grid>
        );
      }

      return (
        <Grid {...containerProps}>
          {date ? <Typography style={{ fontWeight: 500 }}>{formatDateTime(date)}</Typography> : noValue}
        </Grid>
      );
    }

    if (columnType === 'dropbox' || columnType === 'weightedList') {
      const selectedOption = isArray(column.options) ? column.options.find((x) => x.id === dropboxValue) : null;
      const textColor = selectedOption?.color ? theme.palette.getContrastText(selectedOption.color) : undefined;
      if (isArray(column.options) && column.options.length > 0) {
        if (editable) {
          return (
            <Grid {...containerProps}>
              <Button
                style={{
                  textTransform: 'none',
                  justifyContent: textAlign || 'flex-start',
                  color: textColor,
                  background: selectedOption?.color,
                }}
                onClick={disableClick ? undefined : (e) => setMenuAnchor(e.currentTarget)}
                startIcon={<ArrowDropDownRounded style={{ color: textColor }} />}
                fullWidth
                disabled={Boolean(disabled)}
              >
                <Typography noWrap sx={{ fontWeight: 500, color: (theme) => theme.palette.text.secondary }}>
                  {dropboxValue ? selectedOption?.text : 'Pick option'}
                </Typography>
              </Button>

              <SearchMenu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                items={column.options}
                minWidth={columnWidth}
                map={(x) => x}
                onResult={(option) => {
                  if (option === null) {
                    setDropboxValue(null);
                    triggerChange({ value: null }, true);
                    return;
                  }
                  const params = {
                    value: option.id,
                    weight: columnType === 'weightedList' ? option.weight : undefined,
                  };
                  setDropboxValue(option.id);
                  triggerChange(params, true);
                }}
                showClear={dropboxValue}
                withColor
              />
            </Grid>
          );
        }

        return (
          <Grid {...containerProps}>
            {dropboxValue ? (
              <Button
                disabled
                style={{
                  textTransform: 'none',
                  justifyContent: textAlign || 'flex-start',
                  background: selectedOption?.color,
                }}
                fullWidth
              >
                <Typography
                  noWrap
                  sx={{
                    maxWidth: '100%',
                    fontWeight: 500,
                    color: selectedOption?.color
                      ? theme.palette.getContrastText(selectedOption?.color)
                      : (theme) => theme.palette.text.secondary,
                  }}
                >
                  {selectedOption?.text}
                </Typography>
              </Button>
            ) : (
              noValue
            )}
          </Grid>
        );
      } else {
        return <Typography color="textSecondary">Set options</Typography>;
      }
    }

    if (columnType === 'tickbox') {
      return (
        <TickboxCell
          column={column}
          columnWidth={columnWidth}
          disableClick={disableClick}
          disabled={disabled}
          editable={editable}
          initial={initial}
          textAlign={textAlign}
          onChange={triggerChange}
        />
      );
    }

    if (columnType === 'user') {
      if (invitationId && !currUserId) {
        return (
          <ExternalUserCell
            selected={userIds}
            editable={editable}
            users={users}
            onResult={(value) => {
              setUserIds(value);
              triggerChange({ value }, true);
            }}
          />
        );
      }
      if (userIds?.length > 0) {
        return (
          <Grid {...containerProps}>
            <UserGroup
              max={8}
              ids={userIds}
              fullWidth
              onClick={editable && !disabled ? selectUser : undefined}
              avatarSize={30}
            />
          </Grid>
        );
      } else {
        return editable ? (
          <Button
            style={{ textTransform: 'none', justifyContent: textAlign || 'flex-start' }}
            onClick={selectUser}
            startIcon={<PersonRounded />}
            sx={{ color: (theme) => theme.palette.text.secondary }}
            fullWidth
            disabled={Boolean(disabled)}
          >
            Pick users
          </Button>
        ) : (
          noValue
        );
      }
    }

    if (columnType === 'textArea') {
      if (!editable && !text) return noValue;
      return (
        <>
          <Button
            style={{ textTransform: 'none', minWidth: 150, justifyContent: textAlign || 'flex-start' }}
            onClick={() => setDialogOpen(true)}
            startIcon={<SubjectRounded sx={{ color: (theme) => theme.palette.text.secondary }} />}
            fullWidth
            disabled={Boolean(disabled)}
          >
            <Typography
              noWrap={!textWrapping}
              sx={{ maxWidth: '100%', color: (theme) => theme.palette.text.secondary }}
            >
              {textAreaPreview || 'Add text'}
            </Typography>
          </Button>

          <TextDialog
            open={dialogOpen}
            initial={text}
            onClose={() => setDialogOpen(false)}
            editable={editable}
            disableImages
            onResult={(text) => {
              setText(text);
              triggerChange({ value: text }, true);
            }}
          />
        </>
      );
    }

    if (columnType === 'file' || columnType === 'image') {
      if (!editable && files.length === 0) return noValue;
      const imageWidth = 66;
      const limit = Math.floor((columnWidth - 50) / imageWidth);
      const images = files.filter((x) => x.type?.startsWith('image/')).slice(0, limit); // first n pictures
      const remaining = files.length - images.length;
      return (
        <>
          <Button
            style={{ textTransform: 'none', justifyContent: textAlign || 'flex-start' }}
            onClick={disableClick ? undefined : () => setDialogOpen(true)}
            startIcon={images.length > 0 ? undefined : <DescriptionRounded style={{ color: grey[500] }} />}
            fullWidth
            disabled={Boolean(disabled)}
          >
            <Grid container alignItems="center" justifyContent={textAlign}>
              {images.length > 0 ? (
                <>
                  {images.map((image) => (
                    <img
                      key={image.id}
                      style={{
                        maxWidth: imageWidth,
                        maxHeight: 30,
                        userSelect: 'none',
                        pointerEvents: 'none',
                        marginRight: 8,
                      }}
                      src={image.location}
                      alt={image.name}
                    />
                  ))}
                  {remaining > 0 && (
                    <Typography color="textSecondary" variant="h5">
                      +{remaining}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography noWrap style={{ maxWidth: '100%' }}>
                  {files.length > 0 ? `${files.length} attachment${files.length > 1 ? 's' : ''}` : 'Add attachments'}
                </Typography>
              )}
            </Grid>
          </Button>

          <FileDialog
            open={dialogOpen}
            initial={files}
            editable={editable}
            onClose={() => setDialogOpen(false)}
            onResult={(files) => {
              setFiles(files);
              triggerChange({ value: files }, true);
            }}
            onlyImages={columnType === 'image'}
          />
        </>
      );
    }
    if (columnType === 'signature') {
      const hasSignature = Boolean(signature?.base64Data || signature?.location);
      if (!editable && !hasSignature) return noValue;
      return (
        <>
          <Button
            style={{ textTransform: 'none', justifyContent: textAlign || 'flex-start' }}
            onClick={disableClick ? undefined : () => setDialogOpen(true)}
            disabled={Boolean(disabled)}
            startIcon={
              editable ? (
                <EditRounded style={{ color: grey[500] }} />
              ) : (
                <VisibilityRounded style={{ color: grey[500] }} />
              )
            }
          >
            {!hasSignature && <Typography>Add signature</Typography>}
            {hasSignature && (
              <img
                style={{
                  width: 60,
                  height: 30,
                  userSelect: 'none',
                  pointerEvents: 'none',
                  filter:
                    theme.palette.mode === 'dark' && (!signature.type || signature.type === 'signature')
                      ? 'invert(100%)'
                      : '',
                }}
                src={signature?.base64Data || signature?.location}
                alt="signature"
              />
            )}
          </Button>

          <SignatureDialog
            open={dialogOpen}
            initial={signature}
            editable={editable}
            onClose={() => setDialogOpen(false)}
            onResult={(signature) => {
              setSignature(signature);
              triggerChange({ value: signature }, true);
            }}
            onlyImages={columnType === 'image'}
          />
        </>
      );
    }

    if (columnType === 'status') {
      if (building && !forRegister) {
        return (
          <Grid {...containerProps}>
            <Typography gutterBottom color="textSecondary" style={{ fontWeight: 500 }}>
              To be set
            </Typography>
          </Grid>
        );
      }

      const selected = statusValue ? column.options?.find((x) => x.id === statusValue) : null;
      const textColor = selected?.color ? theme.palette.getContrastText(selected.color) : undefined;

      if (editable && !column.readOnly) {
        if (isArray(column.options) && column.options.length > 0) {
          return (
            <>
              <Button
                style={{
                  textTransform: 'none',
                  background: selected ? selected.color : undefined,
                }}
                onClick={disableClick ? undefined : (e) => setMenuAnchor(e.currentTarget)}
                endIcon={
                  <ArrowDropDownRounded sx={{ color: selected ? textColor : (theme) => theme.palette.primary.main }} />
                }
                fullWidth
                variant={selected ? 'text' : 'outlined'}
                disabled={Boolean(disabled)}
              >
                <Typography
                  noWrap
                  sx={{ fontWeight: 500, color: selected ? textColor : (theme) => theme.palette.primary.main }}
                >
                  {selected ? selected.text : 'Select status'}
                </Typography>
              </Button>

              <Menu
                PaperProps={{ style: { minWidth: columnWidth } }}
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
              >
                {isArray(column.options) &&
                  column.options.map((value) => (
                    <MenuItem
                      key={value.id}
                      onClick={() => {
                        setStatusValue(value.id);
                        triggerChange({ value: value.id }, true);
                        setMenuAnchor(null);
                      }}
                    >
                      <ListItemIcon>
                        <FiberManualRecord style={{ color: value.color }} />
                      </ListItemIcon>
                      {value.text}
                    </MenuItem>
                  ))}
                {statusValue && (
                  <MenuItem
                    onClick={() => {
                      setStatusValue(null);
                      triggerChange({ value: null }, true);
                      setMenuAnchor(null);
                    }}
                  >
                    <ListItemIcon>
                      <NotInterested color="error" />
                    </ListItemIcon>
                    Clear
                  </MenuItem>
                )}
              </Menu>
            </>
          );
        }
        return <Typography color="textSecondary">Define statuses</Typography>;
      } else {
        if (!selected) return null;
        return (
          <Button style={{ textTransform: 'none', background: selected.color }} disabled fullWidth>
            <Typography noWrap style={{ fontWeight: 500, color: textColor }}>
              {selected ? selected.text : 'No status'}
            </Typography>
          </Button>
        );
      }
    }

    if (columnType === 'stock') {
      return (
        <StockField
          initial={initial}
          column={column}
          disableClick={disableClick}
          disabled={disabled}
          editable={editable}
          onChange={triggerChange}
        />
      );
    }

    if (columnType === 'dataSet') {
      return (
        <DataSetCell
          params={column.dataSetParams}
          onResult={onDataSetResult}
          disableClick={disableClick || Boolean(disabled)}
          value={{ rows: dataSetRows }}
          editable={editable}
          textAlign={textAlign}
        />
      );
    }

    if (columnType === 'calculation') {
      if (['date'].includes(calculationType)) {
        return (
          <Grid {...containerProps}>
            <Typography style={{ fontWeight: 500 }}>{formatDate(date)}</Typography>
          </Grid>
        );
      } else if (['time'].includes(calculationType)) {
        return (
          <Grid {...containerProps}>
            <Typography style={{ fontWeight: 500 }}>{text}</Typography>
          </Grid>
        );
      } else {
        if (!column?.expressionParams) return <Typography style={{ marginLeft: '0.5rem' }}>-</Typography>;

        const decimal = column.expressionParams.format === 'number' ? column.expressionParams.decimals || 12 : 2;
        const hasValue = number != null;
        return (
          <Grid {...containerProps} alignItems="center" wrap="nowrap">
            <Typography nowrap>
              {hasValue && (
                <>
                  {column.expressionParams?.format === 'dollar' && <>$</>}
                  {column.expressionParams?.format === 'pound' && <>£</>}
                  {column.expressionParams?.format === 'euro' && <>€</>}
                </>
              )}

              {hasValue
                ? ['dollar', 'pound', 'euro'].includes(column.expressionParams?.format)
                  ? round(number, 2).toFixed(2)
                  : round(number, decimal)
                : ''}
            </Typography>
            {column.expressionParams.format === 'percent' && hasValue && (
              <PercentRounded fontSize="small" style={{ color: grey[600] }} />
            )}
          </Grid>
        );
      }
    }

    if (columnType === 'action') {
      return (
        <CellActionButton
          initial={initial?.value}
          params={column.actionParams}
          onChange={(value) => triggerChange({ value }, true)}
          editable={editable}
          rowId={row?.id}
          onDataChange={onDataChange}
          columnId={column.id}
          building={building}
          disabled={actionsDisabled}
        />
      );
    }

    if (columnType === 'actionGroup') {
      return (
        <ActionGroupButton
          activityInfo={activityInfo}
          rowId={row?.id}
          columnId={column.id}
          allCreatedActions={allCreatedActions}
          editable={editable}
          building={building}
          preSetActions={column.preSetActions}
          onResult={onCreatedAction}
          onDeletedAction={onDeletedAction}
          disableClick={disableClick || actionsDisabled}
        />
      );
    }

    if (columnType === 'readAndSign') {
      const useSelectedDocuments = column?.fieldData?.useSpaces || column?.fieldData?.useSubFolders;

      const filesToRead = useSelectedDocuments
        ? initial?.value?.selectedDocuments || []
        : column?.readAndSignOptions?.type === 'docs' && isArray(column.readAndSignOptions.files)
        ? column?.readAndSignOptions.files
        : column?.readAndSignOptions?.type === 'folder' && isArray(column?.fieldData?.files)
        ? column?.fieldData.files
        : [];

      const readDocuments = isArray(initial?.value?.readDocuments) ? initial?.value?.readDocuments : [];

      const notRead = difference(
        filesToRead.map((x) => x.id),
        readDocuments,
      );

      const totalToRead = filesToRead.length;

      const read = totalToRead - notRead.length;

      const allRead = isEmpty(notRead);

      return (
        <Grid container alignItems="center">
          <Button
            onClick={() => setRnsDialog(true)}
            endIcon={totalToRead > 0 && !building && <CheckCircleOutlineRounded />}
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: 0,
              fontSize: '1.1rem',
              color: totalToRead > 0 && allRead ? green[500] : grey[500],
            }}
            disabled={building}
          >
            {building && read === 0 ? 'To be read' : totalToRead > 0 ? `${read}/${totalToRead} read` : 'Pick files'}
          </Button>

          <ReadAndSignDialog
            initial={initial?.value}
            column={column}
            open={rnsDialog}
            onClose={() => setRnsDialog(false)}
            onResult={(value) => triggerChange({ value }, true)}
            editable={editable}
          />
        </Grid>
      );
    }

    if (columnType === 'colorCheck') {
      return (
        <ColorCheckCell initial={initial} onChange={onChange} editable={editable} config={column.colorCheckConfig} />
      );
    }

    if (columnType === 'defectCheck') {
      return <DefectCheckCell initial={initial} onChange={onChange} editable={editable} />;
    }

    // if (columnType === 'linkedFeature') {
    //   console.log(initial);
    //   function handleCreate(data) {
    //     console.log(data);
    //   }
    //   const handleLink = (data) => {
    //     let url = `/${data.targetType}${data.targetType === 'process' ? 'es' : 's'}/${data.targetItem}`;
    //     if (data?.targetType === 'document') {
    //       url = `/DocLibrary/preview/${data.targetItem}`;
    //     }
    //     window.open(url, '_blank');
    //   };

    //   return (
    //     <Grid container item>
    //       <Button color="primary" onClick={() => setLinkedFeatureDialog(true)}>
    //         Add Link
    //       </Button>
    //       <Grid container direction={'column'}>
    //         {initial?.length > 0 &&
    //           initial?.map((doc, i) => (
    //             <Grid container item key={i}>
    //               <TooltipTypography sx={{ width: '150px' }} align="left">
    //                 {doc?.title}
    //               </TooltipTypography>
    //               <TooltipIconButton text="Open Link" onClick={() => handleLink(doc)}>
    //                 <LinkIcon />
    //               </TooltipIconButton>
    //             </Grid>
    //           ))}
    //       </Grid>
    //       <RefLinkDialog
    //         open={linkedFeatureDialog}
    //         handleClose={() => setLinkedFeatureDialog(false)}
    //         handleCreate={handleCreate}
    //       />
    //     </Grid>
    //   );
    // }

    return <Typography color="textSecondary">Type not supported yet</Typography>;
  },
);

export function FileDialog({ open, initial, editable, onClose, onResult, onlyImages, maxFiles }) {
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (!open) return;
    const uploaded = isArray(initial) ? initial : [];
    setFiles([...uploaded]);
  }, [open]); // eslint-disable-line

  function done() {
    onResult(files);
    onClose();
  }

  function close() {
    if (!isEqual(initial, files)) {
      onResult(files);
    }
    onClose();
  }

  return (
    <RoundedDialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Files</DialogTitle>
      <DialogContent>
        <Grid container style={{ overflowX: 'hidden', maxHeight: '60vh' }}>
          <FilesTable files={files} restrictHeight editable={editable} onFilesChange={setFiles} />
        </Grid>
      </DialogContent>
      <StandardDialogActions onClose={close} onDone={done} hideDone={!editable} />
    </RoundedDialog>
  );
}

export function SignatureDialog({ open, initial, editable, onClose, onResult }) {
  const [signature, setSignature] = useState({});

  useEffect(() => {
    setSignature(initial || {});
  }, [initial]);

  function done() {
    onResult(signature);
    onClose();
  }

  return (
    <RoundedDialog open={open} onClose={onClose} maxWidth={initial.type === 'text' ? 'md' : 'sm'} fullWidth>
      <DialogTitle>Signature</DialogTitle>
      <DialogContent>
        {open && (
          <Grid container justifyContent="center">
            <TabbedSignature initial={signature} onResult={setSignature} editable={editable} />
          </Grid>
        )}
      </DialogContent>
      <StandardDialogActions onClose={onClose} onDone={done} hideDone={!editable} />
    </RoundedDialog>
  );
}

function ExternalUserCell({ onResult, selected, editable, style, users }) {
  const [value, setValue] = useState(null);

  useEffect(() => {
    if (!selected || users.length === 0) return;
    const id = isArray(selected) ? selected[0] : selected;
    setValue(users.find((x) => x._id === id) || null);
  }, [selected, users]);

  if (users.length === 0) return null;

  return (
    <Grid container alignItems="center">
      {editable ? (
        <Autocomplete
          options={users}
          value={value}
          style={{ width: 220, ...style }}
          getOptionLabel={(option) => option?.fullName || ''}
          openOnFocus
          renderOption={(props, user) => (
            <li {...props}>
              <Grid container alignItems="center">
                <Avatar
                  src={user.avatar}
                  style={{ background: user.avatarColor, marginRight: 5, width: 25, height: 25 }}
                >
                  {user.avatarLetters}
                </Avatar>
                <Typography style={{ fontSize: '0.75rem' }}>{user.fullName}</Typography>
              </Grid>
            </li>
          )}
          renderInput={(params) => (
            <TextField onKeyDown={(e) => e.stopPropagation()} variant="standard" {...params} label="Select a user" />
          )}
          onChange={(e, x) => {
            setValue(x);
            onResult(x?._id);
          }}
        />
      ) : (
        <>
          <Avatar src={value?.avatar} style={{ background: value?.avatarColor, marginRight: 8 }}>
            {value?.avatarLetters}
          </Avatar>
          <Typography>{value?.fullName}</Typography>
        </>
      )}
    </Grid>
  );
}

export default GridCell;
